import React from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Twitter, Linkedin, Youtube, Send, Heart } from 'lucide-react';

export default function FooterPreview({ attributes = {}, editMode = false, themeColors, previewDevice = 'desktop' }) {
  const {
    brand = { name: 'Brand Name', description: 'Creating exceptional experiences for our customers.' },
    contact = {},
    columns = [
      { id: '1', title: 'Quick Links', links: [
        { label: 'Home', url: '/' }, { label: 'Shop', url: '/shop' },
        { label: 'About', url: '/about' }, { label: 'Contact', url: '/contact' }
      ]},
      { id: '2', title: 'Customer Service', links: [
        { label: 'Shipping & Returns', url: '/shipping_delivery' },
        { label: 'Privacy Policy', url: '/privacy_policy' },
        { label: 'Terms & Conditions', url: '/terms_conditions' },
        { label: 'FAQ', url: '/faq' }
      ]}
    ],
    social = {},
    newsletter = { enabled: true, title: 'Subscribe to Our Newsletter', subtitle: 'Get the latest updates and offers', placeholder: 'Enter your email' },
    copyright = { text: '', year: new Date().getFullYear().toString(), showPaymentIcons: true },
  } = attributes;

  // Derive footer colors from theme
  // For dark templates (RANG, NOIR, VEER), footer uses the theme background
  // For light templates (PRIYA, NEELA), footer uses the primary/dark color as background
  const isDarkTheme = themeColors?.background && ['#0', '#1', '#2'].some(p => themeColors.background.toLowerCase().startsWith(p));
  const accentColor = themeColors?.accent || themeColors?.primary || '#4f46e5';
  const footerBg = isDarkTheme ? themeColors.background : (themeColors?.primary || '#111827');
  const textColor = '#FFFFFF';
  const mutedColor = 'rgba(255,255,255,0.6)';
  const borderColor = isDarkTheme ? (themeColors?.border || 'rgba(255,255,255,0.1)') : 'rgba(255,255,255,0.15)';
  const inputBorderColor = 'rgba(255,255,255,0.15)';
  const inputBgColor = 'rgba(255,255,255,0.08)';
  const socialBgColor = 'rgba(255,255,255,0.1)';
  const newsletterBgColor = 'rgba(255,255,255,0.05)';

  const socialIcons = {
    facebook: Facebook, instagram: Instagram, twitter: Twitter,
    linkedin: Linkedin, youtube: Youtube,
  };

  const hasSocial = Object.entries(social).some(([k, v]) => v && v.startsWith('http'));
  const isMobile = previewDevice === 'mobile';
  const isTablet = previewDevice === 'tablet';

  return (
    <footer style={{
      width: '100%', background: footerBg, color: textColor,
      padding: isMobile ? '32px 16px 16px' : isTablet ? '36px 24px 20px' : '48px 32px 24px',
      fontFamily: 'Inter, system-ui, sans-serif',
    }}>
      {/* Main grid */}
      <div style={{
        display: isMobile ? 'flex' : 'grid',
        flexDirection: isMobile ? 'column' : undefined,
        gridTemplateColumns: isMobile ? undefined : isTablet ? '1.2fr 1fr 1fr' : `1.5fr ${columns.map(() => '1fr').join(' ')}`,
        gap: isMobile ? '24px' : isTablet ? '24px' : '40px',
        maxWidth: '1200px', margin: '0 auto',
      }}>
        {/* Brand column */}
        <div>
          {brand.logo?.imageUrl ? (
            <img src={brand.logo.imageUrl} alt={brand.name} style={{ height: '36px', marginBottom: '12px', objectFit: 'contain' }} />
          ) : (
            <h3 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '12px', color: textColor, margin: '0 0 12px 0' }}>
              {brand.name || 'Brand Name'}
            </h3>
          )}
          <p style={{ fontSize: '13px', color: mutedColor, lineHeight: 1.6, marginBottom: '20px', margin: '0 0 20px 0' }}>
            {brand.description || 'Creating exceptional experiences for our customers.'}
          </p>
          {/* Contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {contact.email && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: mutedColor }}>
                <Mail size={14} /> <span>{contact.email}</span>
              </div>
            )}
            {contact.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: mutedColor }}>
                <Phone size={14} /> <span>{contact.phone}</span>
              </div>
            )}
            {contact.address && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: mutedColor }}>
                <MapPin size={14} /> <span>{contact.address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Link columns */}
        {columns.map((col) => (
          <div key={col.id}>
            <h4 style={{
              fontSize: '12px', fontWeight: 600, textTransform: 'uppercase',
              letterSpacing: '0.08em', color: textColor, marginBottom: '16px',
              margin: '0 0 16px 0',
            }}>
              {col.title}
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(col.links || []).map((link, i) => (
                <span key={i} style={{
                  fontSize: '13px', color: mutedColor, cursor: 'default',
                  transition: 'color 0.15s',
                }}>
                  {link.label}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Newsletter */}
      {newsletter?.enabled && (
        <div style={{
          maxWidth: '1200px', margin: '32px auto 0', padding: '24px',
          background: newsletterBgColor, borderRadius: '12px',
        }}>
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <h4 style={{ fontSize: '15px', fontWeight: 600, color: textColor, margin: '0 0 4px 0' }}>
              {newsletter.title || 'Subscribe to Our Newsletter'}
            </h4>
            <p style={{ fontSize: '12px', color: mutedColor, margin: '4px 0 0 0' }}>
              {newsletter.subtitle || 'Get the latest updates and offers'}
            </p>
          </div>
          <div style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            maxWidth: isMobile ? '100%' : '400px', margin: '0 auto', gap: '8px',
          }}>
            <input
              type="email"
              placeholder={newsletter.placeholder || 'Enter your email'}
              disabled
              style={{
                flex: 1, padding: '10px 14px', borderRadius: '8px',
                border: `1px solid ${inputBorderColor}`, background: inputBgColor,
                color: mutedColor, fontSize: '13px', outline: 'none',
              }}
            />
            <button disabled style={{
              padding: '10px 20px', borderRadius: '8px', border: 'none',
              background: isDarkTheme ? accentColor : '#FFFFFF',
              color: isDarkTheme ? '#FFFFFF' : footerBg,
              fontSize: '13px', fontWeight: 600,
              cursor: 'default', display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              <Send size={14} /> Subscribe
            </button>
          </div>
        </div>
      )}

      {/* Social icons */}
      {hasSocial && (
        <div style={{
          display: 'flex', justifyContent: 'center', gap: '12px',
          margin: '24px auto', maxWidth: '1200px',
        }}>
          {Object.entries(social).map(([platform, url]) => {
            if (!url || !url.startsWith('http')) return null;
            const Icon = socialIcons[platform];
            if (!Icon) return null;
            return (
              <div key={platform} style={{
                width: '36px', height: '36px', borderRadius: '50%',
                background: socialBgColor, display: 'flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'default',
              }}>
                <Icon size={16} style={{ color: mutedColor }} />
              </div>
            );
          })}
        </div>
      )}

      {/* Copyright bar */}
      <div style={{
        borderTop: `1px solid ${borderColor}`, paddingTop: '20px', marginTop: '16px',
        maxWidth: '1200px', margin: '16px auto 0', padding: '20px 0 0 0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <p style={{ fontSize: '12px', color: mutedColor, display: 'flex', alignItems: 'center', gap: '4px', margin: 0 }}>
          &copy; {copyright.year || new Date().getFullYear()} {copyright.text || brand.name || 'Brand Name'}
          <Heart size={10} style={{ color: '#ef4444', fill: '#ef4444' }} />
        </p>
        {copyright.showPaymentIcons && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '10px', color: mutedColor, fontWeight: 600, padding: '2px 6px', border: `1px solid ${borderColor}`, borderRadius: '4px' }}>VISA</span>
            <span style={{ fontSize: '10px', color: mutedColor, fontWeight: 600, padding: '2px 6px', border: `1px solid ${borderColor}`, borderRadius: '4px' }}>MC</span>
            <span style={{ fontSize: '10px', color: mutedColor, fontWeight: 600, padding: '2px 6px', border: `1px solid ${borderColor}`, borderRadius: '4px' }}>UPI</span>
          </div>
        )}
      </div>
    </footer>
  );
}
